import React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import update from 'immutability-helper';
import FormField from 'components/FormField'
import Header from 'components/Header'
import { useAPI } from 'contexts/APIProvider'
import { PracticeProfileForm, WithContactStrings } from 'types/interfaces'
import PracticeProfileError from 'errors/PracticeProfileError'
import CQCLocationSelector from 'components/CQCLocationSelector'
import CQCLocation, { CQC_PREFILLED_FIELDS } from 'logic/CQCLocation'
import ManualAddressFields from 'components/ManualAddressFields'
import Paper from '@mui/material/Paper'
import ErrorPaper from 'components/ErrorAlert'
import { useParams } from 'react-router';
import { isEmpty } from 'lodash';
import { usePractice } from 'contexts/PracticeProvider';
import { useAuthUser } from 'contexts/AuthUserProvider';
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider';
import { Alert, MenuItem } from '@mui/material';
import ErrorList from 'components/ErrorList';
import { MARKETING_CHANNELS, MOBILE_CLINIC_PRACTICE_GROUP_NAME, MULTIPLE_EMAILS_HELPER_TEXT, MULTIPLE_PHONES_HELPER_TEXT } from 'types/constants';
import { toContactLists } from 'logic/helpers';

const PracticeProfileEdit: React.FC = () => {
  const { authUser } = useAuthUser()
  const { practice, reloadPractice } = usePractice()
  const [form, setForm] = React.useState<WithContactStrings<PracticeProfileForm>>({});
  const [error, setError] = React.useState<PracticeProfileError>();
  const { api } = useAPI()
  const { showAlert } = useSnackBarAlert()
  const [loading, setLoading] = React.useState<boolean>(false)
  const [cqcLocation, setCqcLocation] = React.useState<CQCLocation | null>(null)

  const selectCqcLocation = React.useCallback((location: CQCLocation | null) => {
    setCqcLocation(location)
    setForm((form) => update(form, location ? { $merge: location.toPracticeForm() } : { $unset: CQC_PREFILLED_FIELDS }))
    setError((error) => error && update(error, { cqc_location_id: { $set: [] } }))
  }, [])

  const clearError = React.useCallback((name: string) => {
    setError((error) => error && update(error, { [name]: { $set: [] } }))
  }, [])

  const updateForm = React.useCallback((name: string, value: any) => {
    setForm(update(form, { [name]: { $set: value } }))
    if (error) setError(update(error, { [name]: { $set: [] } }))
  }, [error, form])

  const prepopulateForm = React.useCallback(async () => {
    if (!practice) return
    setForm(update(form, {
      'name': { $set: practice.practice_name },
      'cqc_location_id': { $set: practice.cqc_location_id },
      'address': { $set: practice.address },
      'latitude': { $set: practice.latitude },
      'longitude': { $set: practice.longitude },
      'line1': { $set: practice.line1 },
      'line2': { $set: practice.line2 },
      'city': { $set: practice.city },
      'county': { $set: practice.county },
      'country': { $set: practice.country },
      'postcode': { $set: practice.postcode },
      'email_for_inquiries': { $set: practice.email_for_inquiries },
      'email_for_notifications': { $set: practice.email_for_notifications },
      'phone_for_inquiries': { $set: practice.phone_for_inquiries },
      'phone_for_notifications': { $set: practice.phone_for_notifications },
      'affiliate_code': { $set: practice.affiliate_code ?? '' },
      'marketing_channel': { $set: practice.marketing_channel ?? '' },
      'parent_company': { $set: practice.parent_company ?? '' },
      'default_dental_nurse_bonus_rate': { $set: practice.default_dental_nurse_bonus_rate }
    }))
    setCqcLocation(practice.cqc_location)
  }, [form, practice])

  const save = React.useCallback(async () => {
    if (!practice) return
    try {
      setLoading(true)
      await api.editPractice(toContactLists(form), practice.practice_id)
      await reloadPractice()
      showAlert('success', 'Practice Profile Updated')
    } catch (e: any) {
      if (e instanceof PracticeProfileError) {
        setError(e)
      } else {
        console.error('unhandled exception', e)
      }
    } finally {
      setLoading(false)
    }
  }, [api, form, practice, reloadPractice, showAlert])

  React.useEffect(() => {
    prepopulateForm()
  }, [practice])

  if (isEmpty(form) || !practice) {
    return null
  }

  const canEditAddress = (authUser?.isStaff ?? false) || practice.practice_group_name === MOBILE_CLINIC_PRACTICE_GROUP_NAME

  // Errors on fields this role has no input for: address editors render every field except the composite `address`
  const unrenderedErrors = (canEditAddress ? error?.address : error?.cqc_prefilled_errors) ?? []

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
      <Header variant='h1' text='Update Practice Information'></Header>
      <CQCLocationSelector
        label='Enter your practice postcode to find your practice (CQC registered)'
        helperText={canEditAddress ? 'Type the full postcode and select the CQC location to prefill the practice name and address. The fields below override the prefilled values.' : undefined}
        value={cqcLocation}
        onChange={selectCqcLocation}
        errors={error?.cqc_location_id}
      />
      {unrenderedErrors.length ? (
        <Alert severity='error'><ErrorList errors={unrenderedErrors} /></Alert>
      ) : null}
      {!canEditAddress && !cqcLocation ? (
        <Paper sx={{ whiteSpace: 'pre-line', p: 1 }}>
          {[practice.practice_name, practice.address].filter(Boolean).join('\n')}
        </Paper>
      ) : null}
      { canEditAddress ? (
        <React.Fragment>
          <FormField
            name='name'
            label='Practice Name'
            onChange={(e) => updateForm('name', e.target.value)}
            value={form.name ?? ''}
            errors={error?.name}
          />
          <ManualAddressFields
            form={form}
            error={error}
            setForm={setForm}
            clearError={clearError}
          />
        </React.Fragment>
      ) : null }
      <FormField
        name='parent_company'
        label='Practice Group Name'
        helperText='If you are part of a group of practices, enter the name of the group here. If you are independent, leave this blank.'
        onChange={(e) => updateForm('parent_company', e.target.value)}
        value={form.parent_company}
        errors={error?.parent_company}
      />
      <FormField
        name='phone_for_inquiries'
        label='Phone Number (General Inquiries)'
        onChange={(e) => updateForm('phone_for_inquiries', e.target.value)}
        value={form.phone_for_inquiries}
        errors={error?.phone_for_inquiries}
        helperText={MULTIPLE_PHONES_HELPER_TEXT}
      />
      <FormField
        name='phone_for_notifications'
        label='Phone Number (to receive SMS notifications)'
        onChange={(e) => updateForm('phone_for_notifications', e.target.value)}
        value={form.phone_for_notifications}
        errors={error?.phone_for_notifications}
        helperText={MULTIPLE_PHONES_HELPER_TEXT}
      />
      <FormField
        name='email_for_inquiries'
        label='Email (General Inquiries)'
        onChange={(e) => updateForm('email_for_inquiries', e.target.value)}
        value={form.email_for_inquiries}
        errors={error?.email_for_inquiries}
        helperText={MULTIPLE_EMAILS_HELPER_TEXT}
      />
      <FormField
        name='email_for_notifications'
        label='Email (to receive notifications)'
        onChange={(e) => updateForm('email_for_notifications', e.target.value)}
        value={form.email_for_notifications}
        errors={error?.email_for_notifications}
        helperText={MULTIPLE_EMAILS_HELPER_TEXT}
      />
      { authUser?.isStaff ? (
        <React.Fragment>
          <FormField
            name='affiliate_code'
            label='Affiliate Code'
            onChange={(e) => updateForm('affiliate_code', e.target.value)}
            value={form.affiliate_code}
            errors={error?.affiliate_code}
            helperText="Enter 'none' to unset affiliate code. Don't change code if there are existing affiliated transactions, otherwise the records may mess up"
          />
          <FormField
            name='default_dental_nurse_bonus_rate'
            label='Default Bonus Rate - Dental Nurse (£/hr)'
            type='number'
            onChange={(e) => updateForm('default_dental_nurse_bonus_rate', e.target.value)}
            value={form.default_dental_nurse_bonus_rate ?? ''}
            errors={error?.default_dental_nurse_bonus_rate}
          />
        </React.Fragment>
      ) : null }
      <FormField
        select
        name='marketing_channel'
        label='How did you hear about Locumloop?'
        onChange={(e) => updateForm('marketing_channel', e.target.value)}
        value={form.marketing_channel ?? ''}
        errors={error?.marketing_channel}
      >
        <MenuItem value=''>Please select</MenuItem>
        {MARKETING_CHANNELS.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </FormField>
      <ErrorPaper errors={error?.schema}></ErrorPaper>
      <Button variant="contained" color="primary" onClick={save} disabled={loading}>
        {loading ? 'Please Wait ...' : 'Save'}
      </Button>
    </Box>
  )
}

export default PracticeProfileEdit